import type { ReactNode } from 'react';
import { IconChevronRight } from '../icons';

/**
 * The top of every list screen, in the order the eye reads it: where am I, what is this,
 * how many, and the one thing I am most likely here to do.
 */
export function PageHeader({
  crumbs,
  title,
  subtitle,
  action,
  aside,
}: {
  crumbs?: string[];
  title: string;
  subtitle?: ReactNode;
  /** The single primary action. One per page — a second one makes neither of them primary. */
  action?: ReactNode;
  /** Status, not action: the sync chip, a count, a warning. */
  aside?: ReactNode;
}) {
  return (
    <header className="nv-page-head">
      {crumbs && crumbs.length > 0 ? (
        <nav className="nv-breadcrumb" aria-label={title}>
          {crumbs.map((crumb, i) => (
            <span key={crumb} className="nv-crumb" aria-current={i === crumbs.length - 1 ? 'page' : undefined}>
              {i > 0 ? <IconChevronRight size={14} /> : null}
              {crumb}
            </span>
          ))}
        </nav>
      ) : null}

      <div className="nv-page-header">
        <div className="nv-page-header__text">
          <h1 className="nv-h1">{title}</h1>
          {subtitle ? <p className="nv-subtitle">{subtitle}</p> : null}
        </div>
        <div className="nv-page-header__right">
          {aside}
          {action}
        </div>
      </div>
    </header>
  );
}

/** The card everything else sits in: toolbar on top, scrolling body, fixed footer. */
export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={className ? `nv-card ${className}` : 'nv-card'}>{children}</section>;
}

/** Search on the left, filters beside it, the result count on the right. */
export function Toolbar({ children, count }: { children?: ReactNode; count?: ReactNode }) {
  return (
    <div className="nv-toolbar">
      {children}
      <span className="nv-toolbar__spacer" />
      {count != null ? <span className="nv-toolbar__count">{count}</span> : null}
    </div>
  );
}
